/* eslint-disable */
import React, { useEffect, useState } from "react";
import AbsenceDecisionNotePanel, {
  analyseAbsenceDecisionNote,
} from "./AbsenceDecisionNotePanel";
import AbsenceDecisionTimeline from "./AbsenceDecisionTimeline";

function toDate(value) {
  try {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatDate(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function niceText(value) {
  if (!value) return "—";
  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeStatus(value) {
  return String(value || "submitted").toLowerCase().trim();
}

function statusLabel(value) {
  const s = normalizeStatus(value);
  if (s === "approved" || s === "resolved") return "Approved";
  if (s === "rejected") return "Rejected";
  if (s === "reviewing") return "Under review";
  return "Submitted";
}

function getExplanation(report) {
  return (
    report?.notes ||
    report?.note ||
    report?.message ||
    report?.explanation ||
    report?.description ||
    ""
  );
}

function hasProof(report) {
  return !!(report?.proofPath || report?.proofUrl || report?.proofName);
}

function getCountdown(report) {
  if (!hasProof(report)) return "No supporting document";
  if (report?.proofDeletedAt) {
    return `Deleted ${formatDateTime(report.proofDeletedAt)}`;
  }

  const d = toDate(report?.proofDeleteAfter);
  if (!d) return "Deletes after decision";

  const diff = d.getTime() - Date.now();
  if (diff <= 0) return "Due for deletion";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h left`;
  return `${hours}h ${mins}m left`;
}

function StatusPill({ status }) {
  const s = normalizeStatus(status);

  const tone =
    s === "approved" || s === "resolved"
      ? styles.approvedPill
      : s === "rejected"
      ? styles.rejectedPill
      : s === "reviewing"
      ? styles.reviewingPill
      : styles.submittedPill;

  return (
    <span style={{ ...styles.statusPill, ...tone }}>
      {statusLabel(status)}
    </span>
  );
}


function InfoCard({ label, value }) {
  return (
    <div style={styles.infoCard}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function AbsenceModal({
  report,
  schoolId,
  studentName,
  parentName,
  busy,
  onClose,
  onDecision,
  onOpenEvidence,
}) {
  const [showRejectNote, setShowRejectNote] = useState(false);
  const [decisionNote, setDecisionNote] = useState("");
  const [noteAnalysis, setNoteAnalysis] = useState(
    analyseAbsenceDecisionNote("")
  );

  useEffect(() => {
    setShowRejectNote(false);
    setDecisionNote(report?.decisionNote || "");
    setNoteAnalysis(analyseAbsenceDecisionNote(report?.decisionNote || ""));
  }, [report?.id, report?.decisionNote]);

  if (!report) return null;

  const explanation = getExplanation(report);
  const priority = report.priority || {};
  const proofAttached = hasProof(report);
  const rejectBlocked = showRejectNote && noteAnalysis?.isBlocked;

  function approve() {
    onDecision(report, "approved");
  }

  function markReviewing() {
    onDecision(report, "reviewing");
  }

  function rejectClick() {
    if (!showRejectNote) {
      setShowRejectNote(true);
      return;
    }

    if (noteAnalysis?.isBlocked) return;

    onDecision(report, "rejected", {
      decisionNote,
      decisionTone: noteAnalysis?.tone || "good",
      decisionFlags: noteAnalysis?.flags || [],
    });
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.hero}>
          <div>
            <p style={styles.eyebrow}>Absence review</p>
            <h2 style={styles.title}>{studentName || "Student"}</h2>
            <p style={styles.sub}>
              Submitted by {parentName || "Parent"} •{" "}
              {formatDate(report.date || report.dateKey || report.createdAt)}
            </p>
          </div>

          <button type="button" style={styles.closeTop} onClick={onClose}>
            <span style={{ transform: "translateY(-1px)" }}>×</span>
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.summaryGrid}>
            <InfoCard label="Status" value={<StatusPill status={report.status} />} />
            <InfoCard label="Reason" value={niceText(report.reason)} />
            <InfoCard label="Duration" value={niceText(report.duration || "Full day")} />
            <InfoCard label="Retention" value={getCountdown(report)} />
          </div>

          <section style={styles.priorityCard}>
            <div style={styles.priorityScore}>{priority.score || 0}</div>

            <div>
              <p style={styles.cardLabel}>Priority engine</p>
              <h3 style={styles.cardTitle}>{priority.level || "Normal"}</h3>

              <div style={styles.flags}>
                {(priority.flags || []).length ? (
                  priority.flags.map((flag) => (
                    <span key={flag} style={styles.flag}>
                      {flag}
                    </span>
                  ))
                ) : (
                  <span style={styles.flag}>No priority flags</span>
                )}
              </div>
            </div>
          </section>

          <section style={styles.card}>
            <p style={styles.cardLabel}>Parent explanation</p>
            <p style={styles.longText}>
              {explanation || "No additional information provided."}
            </p>
          </section>

          <section style={styles.card}>
            <div style={styles.cardTop}>
              <div>
                <p style={styles.cardLabel}>Supporting document (optional)</p>
                <h3 style={styles.cardTitle}>
                  {proofAttached
                    ? report.proofName || "Uploaded document"
                    : "No document uploaded"}
                </h3>
              </div>

              <span
                style={
                  proofAttached ? styles.documentBadge : styles.noDocumentBadge
                }
              >
                {proofAttached ? "Temporary review" : "None"}
              </span>
            </div>

            <p style={styles.longText}>
              Supporting documents are used only for temporary review and are
              automatically deleted after the school’s decision window.
            </p>

{proofAttached && !report.proofDeletedAt ? (
  <button
    type="button"
    onClick={() => onOpenEvidence?.(report)}
    style={styles.openProof}
    disabled={busy || typeof onOpenEvidence !== "function"}
  >
    Open supporting document
  </button>
) : null}

            {report.proofDeletedAt ? (
              <div style={styles.deletedBox}>
                Document deleted. Only metadata remains.
              </div>
            ) : null}
          </section>

          {showRejectNote ? (
            <AbsenceDecisionNotePanel
              value={decisionNote}
              onChange={setDecisionNote}
              onToneChange={setNoteAnalysis}
            />
          ) : null}

          {rejectBlocked ? (
            <div style={styles.blockedRejectBox}>
              Confirm reject is disabled until the note is rewritten using neutral,
              factual wording.
            </div>
          ) : null}

          <AbsenceDecisionTimeline history={report.decisionHistory || []} />
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.secondaryBtn}
            disabled={busy}
            onClick={markReviewing}
          >
            Mark reviewing
          </button>

          <button
            type="button"
            style={styles.approveBtn}
            disabled={busy}
            onClick={approve}
          >
            Approve absence
          </button>

          <button
            type="button"
            style={{
              ...styles.rejectBtn,
              ...(rejectBlocked || busy ? styles.disabledBtn : {}),
            }}
            disabled={busy || rejectBlocked}
            onClick={rejectClick}
            title={
              rejectBlocked
                ? "Rewrite the note using neutral wording before confirming."
                : ""
            }
          >
            {showRejectNote ? "Confirm reject" : "Reject absence"}
          </button>

          <button type="button" style={styles.closeBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.58)",
    backdropFilter: "blur(10px)",
    zIndex: 5000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 18,
  },

  modal: {
    width: "100%",
    maxWidth: 860,
    maxHeight: "90vh",
    overflow: "hidden",
    background: "#ffffff",
    borderRadius: 32,
    boxShadow: "0 40px 120px rgba(15, 23, 42, 0.38)",
    border: "1px solid rgba(255,255,255,0.55)",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
  },

  hero: {
    padding: 26,
    color: "#ffffff",
    background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
  },

  eyebrow: {
    margin: 0,
    fontSize: 11,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontWeight: 950,
    opacity: 0.82,
  },

  title: {
    margin: "8px 0 0",
    fontSize: 34,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "-0.04em",
  },

  sub: {
    margin: "10px 0 0",
    opacity: 0.9,
    lineHeight: 1.5,
  },

  closeTop: {
    width: 46,
    height: 46,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.26)",
    background: "rgba(255,255,255,0.15)",
    color: "#fff",
    fontSize: 28,
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    padding: 0,
    flexShrink: 0,
  },

  body: {
    padding: 22,
    overflowY: "auto",
    display: "grid",
    gap: 14,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
  },

  infoCard: {
    padding: 14,
    borderRadius: 20,
    background: "#f8fbff",
    border: "1px solid #e5ebf7",
    display: "grid",
    gap: 7,
    minHeight: 74,
  },

  priorityCard: {
    padding: 18,
    borderRadius: 24,
    background: "linear-gradient(135deg, #eef4ff 0%, #f8fbff 100%)",
    border: "1px solid #dce7fb",
    display: "flex",
    gap: 16,
    alignItems: "center",
  },

  priorityScore: {
    width: 66,
    height: 66,
    borderRadius: 22,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
    color: "#fff",
    fontSize: 28,
    fontWeight: 950,
    flexShrink: 0,
  },

  card: {
    padding: 18,
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #e5ebf7",
    boxShadow: "0 12px 30px rgba(20,54,110,0.05)",
  },

  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },

  cardLabel: {
    margin: 0,
    fontSize: 11,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#64748b",
    fontWeight: 950,
  },

  cardTitle: {
    margin: "6px 0 0",
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 950,
  },

  longText: {
    margin: "10px 0 0",
    color: "#334155",
    lineHeight: 1.65,
    whiteSpace: "pre-wrap",
  },

  flags: {
    display: "flex",
    gap: 7,
    flexWrap: "wrap",
    marginTop: 10,
  },

  flag: {
    borderRadius: 999,
    padding: "6px 10px",
    background: "#eef2ff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
  },

  statusPill: {
    width: "fit-content",
    borderRadius: 999,
    padding: "7px 11px",
    fontSize: 12,
    fontWeight: 950,
  },

  approvedPill: {
    background: "#dcfae6",
    color: "#027a48",
  },

  rejectedPill: {
    background: "#fee4e2",
    color: "#b42318",
  },

  reviewingPill: {
    background: "#dbeafe",
    color: "#175cd3",
  },

  submittedPill: {
    background: "#fef0c7",
    color: "#b54708",
  },

  documentBadge: {
    borderRadius: 999,
    padding: "7px 11px",
    background: "#edf4ff",
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 950,
  },

  noDocumentBadge: {
    borderRadius: 999,
    padding: "7px 11px",
    background: "#f1f5f9",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 950,
  },

  openProof: {
    marginTop: 14,
    display: "inline-flex",
    textDecoration: "none",
    background: "#edf4ff",
    color: "#1d4ed8",
    padding: "12px 15px",
    borderRadius: 15,
    fontWeight: 950,
  },

  deletedBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 16,
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: 850,
  },

  blockedRejectBox: {
    padding: 13,
    borderRadius: 16,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.5,
  },

  footer: {
    padding: 16,
    background: "#ffffff",
    borderTop: "1px solid #e5ebf7",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },

  secondaryBtn: {
    border: "none",
    background: "#dbeafe",
    color: "#175cd3",
    padding: "11px 14px",
    borderRadius: 14,
    fontWeight: 950,
    cursor: "pointer",
  },

  approveBtn: {
    border: "none",
    background: "#dcfae6",
    color: "#027a48",
    padding: "11px 14px",
    borderRadius: 14,
    fontWeight: 950,
    cursor: "pointer",
  },

  rejectBtn: {
    border: "none",
    background: "#fee4e2",
    color: "#b42318",
    padding: "11px 14px",
    borderRadius: 14,
    fontWeight: 950,
    cursor: "pointer",
  },

  disabledBtn: {
    opacity: 0.55,
    cursor: "not-allowed",
    filter: "grayscale(0.2)",
  },

  closeBtn: {
    border: "1px solid #d8e1f2",
    background: "#ffffff",
    color: "#334155",
    padding: "11px 14px",
    borderRadius: 14,
    fontWeight: 950,
    cursor: "pointer",
  },
};